import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { pulseTheme } from '@pulse/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { LoginPage } from './features/auth/pages/LoginPage';
import { HomePage } from './features/home/pages/HomePage';
import { TimesheetPage } from './features/time/pages/TimesheetPage';
import { AppLayout } from './layout/AppLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes — sensible default for business data
      retry: 1,
    },
  },
});

// Domain feature routes are added here as modules are implemented.
// Pattern: lazy-import the domain's routes.tsx and wrap in Suspense.
const PreSalesRoutes = React.lazy(() => import('./features/presales/routes'));
const AccountRoutes = React.lazy(() => import('./features/account/routes'));
const AccountsRoutes = React.lazy(() => import('./features/accounts/routes'));
const CommonRoutes = React.lazy(() => import('./features/common/routes'));
const AccessManagementRoutes = React.lazy(() => import('./features/access-management/routes'));
const DataImportRoutes = React.lazy(() => import('./features/admin-data-import/routes'));
const AuditLogRoutes = React.lazy(() => import('./features/audit-log/routes'));
const NotificationRoutes = React.lazy(() => import('./features/notifications/routes'));
const NotificationSchemesRoutes = React.lazy(() => import('./features/notification-schemes/routes'));
const OrganizationRoutes = React.lazy(() => import('./features/organization/routes'));
const ProjectsRoutes = React.lazy(() => import('./features/projects/routes'));
const ScheduledJobsRoutes = React.lazy(() => import('./features/scheduled-jobs/routes'));
const DeliveryRoutes = React.lazy(() => import('./features/delivery/routes'));
const FinanceRoutes = React.lazy(() => import('./features/finance/routes'));
const ReportingRoutes = React.lazy(() => import('./features/reporting/routes'));

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={pulseTheme}>
          <CssBaseline />
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/login" element={<LoginPage />} />
                {/* Single layout route wraps every protected page with ProtectedRoute + the
                side-nav shell — add new domain routes as children here, not by repeating
                ProtectedRoute/AppLayout per route. Register the nav entry itself in
                src/config/navigation.tsx. */}
                <Route
                  element={
                    <ProtectedRoute>
                      {/* Wraps AppLayout rather than sitting inside it so AppLayout itself can
                      read the context (the nav's unread badge) — a component can't consume a
                      provider it renders. Scoped to this route, so the notification stream
                      still runs only on protected pages, never on /login. */}
                      <NotificationProvider>
                        <AppLayout />
                      </NotificationProvider>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/timesheet" element={<TimesheetPage />} />
                  <Route
                    path="/notifications/*"
                    element={
                      <React.Suspense fallback={null}>
                        <NotificationRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/pre-sales/*"
                    element={
                      <React.Suspense fallback={null}>
                        <PreSalesRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/accounts/*"
                    element={
                      <React.Suspense fallback={null}>
                        <AccountsRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/accounts/*"
                    element={
                      <React.Suspense fallback={null}>
                        <AccountRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/common/*"
                    element={
                      <React.Suspense fallback={null}>
                        <CommonRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/access-management/*"
                    element={
                      <React.Suspense fallback={null}>
                        <AccessManagementRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/data-import/*"
                    element={
                      <React.Suspense fallback={null}>
                        <DataImportRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/audit-log/*"
                    element={
                      <React.Suspense fallback={null}>
                        <AuditLogRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/notifications/*"
                    element={
                      <React.Suspense fallback={null}>
                        <NotificationSchemesRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/organization/*"
                    element={
                      <React.Suspense fallback={null}>
                        <OrganizationRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/projects/*"
                    element={
                      <React.Suspense fallback={null}>
                        <ProjectsRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/admin/scheduled-jobs/*"
                    element={
                      <React.Suspense fallback={null}>
                        <ScheduledJobsRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/delivery/*"
                    element={
                      <React.Suspense fallback={null}>
                        <DeliveryRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/finance/*"
                    element={
                      <React.Suspense fallback={null}>
                        <FinanceRoutes />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/reporting/*"
                    element={
                      <React.Suspense fallback={null}>
                        <ReportingRoutes />
                      </React.Suspense>
                    }
                  />
                  {/* Domain routes — add here as features are built:
                  <Route path="/resources/*"   element={...} />
                  <Route path="/time/*"        element={...} />
                  */}
                </Route>
                <Route path="*" element={<div>404 — Page not found</div>} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
        {/* Dev-only — its fixed top-right toggle button otherwise sits over any page's own
            top-right "Add" button on a narrow viewport (production builds/e2e's vite preview
            never mounted a dev server, but this component had no env guard of its own). */}
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
